import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type { ApiError } from '@/types';
import { showPermissionToast } from '@/utils/permission-toast';

// --- snake_case → camelCase transformation ---

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

export function transformKeys<T>(data: unknown): T {
  if (data === null || data === undefined) return data as T;
  if (Array.isArray(data)) return data.map((item) => transformKeys(item)) as T;
  if (typeof data === 'object' && data !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      result[snakeToCamel(key)] = transformKeys(value);
    }
    return result as T;
  }
  return data as T;
}

// --- Retry logic ---

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

interface RetryConfig {
  retryCount?: number;
}

function isRetryable(error: AxiosError): boolean {
  const status = error.response?.status;
  if (!status) return true; // network error
  return status === 429 || status >= 500;
}

function getRetryDelay(attempt: number, error: AxiosError): number {
  if (error.response?.status === 429) {
    const retryAfter = error.response.headers['retry-after'];
    if (retryAfter) return Number(retryAfter) * 1000;
  }
  return BASE_DELAY_MS * Math.pow(2, attempt);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// --- Debounce utility ---

export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  delayMs = 300,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}

// --- Axios instance ---

function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL as string,
    timeout: 10_000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor — attach JWT (if logged in) or API key, plus headers
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Try to get stored session token (set by auth-context after login)
    const storedToken = sessionStorage.getItem('xs_auth_token');
    if (storedToken) {
      config.headers.set('Authorization', `Bearer ${storedToken}`);
    } else {
      const apiKey = import.meta.env.VITE_API_KEY as string;
      if (apiKey) {
        config.headers.set('Authorization', `Bearer ${apiKey}`);
      }
    }
    config.headers.set('X-API-Version', '2');
    config.headers.set('x-user-role', 'admin');
    return config;
  });

  // Response interceptor — normalize to ApiResponse<T> and handle errors
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      // Handle 403 Forbidden — show toast, do not redirect
      if (error.response?.status === 403) {
        const data = error.response.data as Record<string, unknown> | undefined;
        if (data?.code === 'APPROVAL_REQUIRED') {
          showPermissionToast({
            message: 'This action requires approval from an administrator',
            severity: 'warning',
          });
        } else {
          showPermissionToast({
            message: "You don't have permission for this action",
            severity: 'error',
          });
        }
        return Promise.reject(normalizeError(error));
      }

      const config = error.config as (InternalAxiosRequestConfig & RetryConfig) | undefined;
      if (!config) return Promise.reject(normalizeError(error));

      const retryCount = config.retryCount ?? 0;

      if (isRetryable(error) && retryCount < MAX_RETRIES) {
        config.retryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount, error);
        await sleep(delay);
        return client.request(config);
      }

      return Promise.reject(normalizeError(error));
    },
  );

  return client;
}

// --- Error normalization ---

export class ApiRequestError extends Error {
  code: string;
  details?: Record<string, unknown>;
  statusCode?: number;

  constructor(apiError: ApiError, statusCode?: number) {
    super(apiError.message);
    this.name = 'ApiRequestError';
    this.code = apiError.code;
    this.details = apiError.details;
    this.statusCode = statusCode;
  }
}

function normalizeError(error: AxiosError): ApiRequestError {
  const status = error.response?.status;
  const data = error.response?.data as Record<string, unknown> | undefined;

  // Extract message from various backend error response shapes:
  // { message: "..." } or { error: { code, message } } or { error: "string" }
  const errorField = data?.error;
  let errorMessage: string;

  if (typeof data?.message === 'string') {
    errorMessage = data.message;
  } else if (typeof errorField === 'string') {
    errorMessage = errorField;
  } else if (typeof errorField === 'object' && errorField !== null) {
    const nested = errorField as Record<string, unknown>;
    errorMessage = typeof nested.message === 'string' ? nested.message : (error.message || 'An unexpected error occurred');
  } else {
    errorMessage = error.message || 'An unexpected error occurred';
  }

  // Extract error code from nested error object if available
  const errorCode =
    (typeof errorField === 'object' && errorField !== null
      ? String((errorField as Record<string, unknown>).code ?? '')
      : '') || String(status ?? 'NETWORK_ERROR');

  const apiError: ApiError = {
    code: errorCode,
    message: errorMessage,
    details: data,
  };

  return new ApiRequestError(apiError, status);
}

export const apiClient = createApiClient();
